import React, { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../Context";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Tabs,
  Input,
  Button,
  Progress,
  Tag,
  List,
  Empty,
  Spin,
  message,
  Modal,
  Tooltip,
  Popover,
  Skeleton,
  Drawer,
} from "antd";
import {
  LinkOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  ReloadOutlined,
  HistoryOutlined,
  SearchOutlined,
  BulbOutlined,
  GlobalOutlined,
  FileWordOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { executeRecaptcha, preloadRecaptcha } from "../../utils/recaptcha";
import { useIsAuthenticated, isAuthenticated } from "../../utils/auth";
import "./geoScan.scss";

// ── Types ─────────────────────────────────────────────────────────────────────

type Improvement = {
  signal: string;
  fix: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
};

type ScanResult = {
  scanId: string;
  inputType: "url" | "text";
  score: {
    blogScore: number;
    grade: "A" | "B" | "C" | "D" | "F";
    summary: string;
    breakdown: Record<string, number>;
  };
  signals: Record<string, any>;
  suggestions: Improvement[];
  content: {
    title: string;
    wordCount: number;
    siteName: string | null;
    byline: string | null;
  };
};

type HistoryItem = {
  id: string;
  inputType: "url" | "text";
  inputUrl: string | null;
  contentTitle: string | null;
  wordCount: number;
  geoScore: number;
  blogScore?: number;
  grade?: string;
  createdAt: string;
};

// ── Constants & helpers ───────────────────────────────────────────────────────

const API = process.env.REACT_APP_SERVER_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

// Mode is decided at runtime by auth state. Anonymous → freemium (locked +
// Stripe unlock). Logged-in → paid (credits, full result). Same component,
// same build serves both audiences.
const publicHeader = () => ({});

// Labels cover both URL-mode (42-signal) and text-mode (22-signal) scorer
// signal keys. Text mode adds writingClarity, hookStrength, conclusionStrength,
// comparisonStructure, statisticalClaims — without these the suggestions list
// would render raw camelCase keys.
const SIGNAL_LABELS: Record<string, string> = {
  wordCount: "Word Count",
  originalityDepth: "Originality & Depth",
  factualAccuracy: "Factual Accuracy",
  schemaMarkup: "Schema Markup",
  metaTags: "Meta Tags",
  headingStructure: "Heading Structure",
  singleH1: "Single H1",
  questionH2s: "Question H2s",
  primaryKeywordClarity: "Keyword Clarity",
  faqContentSection: "FAQ Section",
  faqPageSchema: "FAQ Schema",
  noGenericFaqs: "No Generic FAQs",
  aiBotAccess: "AI Bot Access",
  llmsTxt: "llms.txt",
  entityDensity: "Entity Density",
  personSchema: "Person Schema",
  howToSchema: "HowTo Schema",
  itemListSchema: "ItemList Schema",
  speakableSchema: "Speakable Schema",
  directAnswerIntro: "Direct-Answer Intro",
  tldrBlock: "TL;DR Block",
  tableOfContents: "Table of Contents",
  comparisonTable: "Comparison Table",
  anchorLinks: "Anchor Links",
  internalLinks: "Internal Links",
  outboundAuthorityLinks: "Outbound Authority Links",
  pillarLinking: "Pillar Linking",
  authorBio: "Author Bio",
  reviewerLine: "Reviewer Line",
  authorArchivePageLink: "Author Archive Link",
  imageAltText: "Image Alt Text",
  uniqueVisuals: "Unique Visuals",
  downloadableAssets: "Downloadable Assets",
  firstHandExperience: "First-Hand Experience",
  originalExamples: "Original Examples",
  caseStudyCallouts: "Case Study Callouts",
  noFictionalQuotes: "No Fictional Quotes",
  avoidWeakClaims: "Avoid Weak Claims",
  naturalKeywords: "Natural Keywords",
  noEmDashes: "No Em Dashes",
  readingTime: "Reading Time",
  changelogBlock: "Changelog Block",
  // ── Text-mode (content scorer) additions ──
  writingClarity: "Writing Clarity",
  hookStrength: "Hook Strength",
  conclusionStrength: "Conclusion Strength",
  comparisonStructure: "Comparison Structure",
  statisticalClaims: "Statistical Claims",
};

// Darker shade paired with each tier color for score-number gradient.
const TIER_DARK: Record<string, string> = {
  F: "#b91c1c", // deep red
  D: "#c2410c", // deep orange
  C: "#a16207", // deep yellow/amber
  B: "#4d7c0f", // deep lime
  A: "#059669", // deep emerald
};

const getScoreGradient = (score: number) => {
  const tier =
    score >= 85
      ? "A"
      : score >= 70
        ? "B"
        : score >= 55
          ? "C"
          : score >= 40
            ? "D"
            : "F";
  const base = QUALITY_TIERS.find((t) => t.grade === tier)?.color ?? "#dc2626";
  return { "0%": base, "100%": TIER_DARK[tier] ?? base };
};

const getGradeColor = (g: string) =>
  QUALITY_TIERS.find((t) => t.grade === g)?.color ?? "#dc2626";

const getPriorityBadge = (p: string) => {
  if (p === "HIGH") return { color: "error", icon: <CloseCircleOutlined /> };
  if (p === "MEDIUM")
    return { color: "warning", icon: <ExclamationCircleOutlined /> };
  return { color: "default", icon: <CheckCircleOutlined /> };
};

// Grade thresholds match backend (blogScorer.js): A ≥85, B ≥70, C ≥55, D ≥40, else F.
// Frontend maps grade → quality label for user-friendly display. Backend `grade` field untouched.
type QualityTier = {
  grade: string;
  label: string;
  range: string;
  min: number;
  color: string;
  tip: string;
};

const QUALITY_TIERS: QualityTier[] = [
  {
    grade: "F",
    label: "Poor",
    range: "0–39",
    min: 0,
    color: "#ef4444",
    tip: "Focus on core content depth, structure, and AI access basics.",
  },
  {
    grade: "D",
    label: "Needs Work",
    range: "40–54",
    min: 40,
    color: "#f97316",
    tip: "Add missing schemas, FAQ section, and improve heading hierarchy.",
  },
  {
    grade: "C",
    label: "Average",
    range: "55–69",
    min: 55,
    color: "#facc15",
    tip: "Sharpen author credibility, add case studies, and a TL;DR block.",
  },
  {
    grade: "B",
    label: "Good",
    range: "70–84",
    min: 70,
    color: "#a3e635",
    tip: "Add first-hand experience, custom visuals, and grounded citations.",
  },
  {
    grade: "A",
    label: "Excellent",
    range: "85–100",
    min: 85,
    color: "#22c55e",
    tip: "Maintain freshness — update changelog and re-verify facts periodically.",
  },
];

const getQualityTier = (grade: string): QualityTier =>
  QUALITY_TIERS.find((t) => t.grade === grade) ?? QUALITY_TIERS[0];

const getNextTierGap = (
  score: number,
): { label: string; gap: number } | null => {
  const next = QUALITY_TIERS.find((t) => score < t.min);
  return next ? { label: next.label, gap: next.min - score } : null;
};

// Custom brand icon for Blog Auditor — semi-circle gauge inside solid brand-orange badge.
const AuditorBrandIcon: React.FC = () => (
  <span className="auditor-brand-icon" aria-hidden>
    <svg viewBox="0 0 44 44" width="44" height="44">
      <rect x="2" y="2" width="40" height="40" rx="12" fill="#ff7c02" />
      {/* Semi-circle gauge track */}
      <path
        d="M11 27 A 11 11 0 0 1 33 27"
        fill="none"
        stroke="#fff"
        strokeOpacity="0.35"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      {/* Filled progress arc (about 75%) */}
      <path
        d="M11 27 A 11 11 0 0 1 30.4 20.5"
        fill="none"
        stroke="#fff"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      {/* Needle */}
      <line
        x1="22"
        y1="27"
        x2="30.4"
        y2="20.5"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      {/* Hub */}
      <circle cx="22" cy="27" r="2.6" fill="#fff" />
    </svg>
  </span>
);

// Premium semi-circle speedometer — full-color tier segments, tapered needle in tier color,
// tier label chips placed radially outside the arc at each tier midpoint.
const ScoreGauge: React.FC<{ score: number; grade: string }> = ({
  score,
  grade,
}) => {
  const s = Math.max(0, Math.min(100, score));
  const cx = 150,
    cy = 150,
    r = 115,
    sw = 26;
  const toAngle = (v: number) => 180 - (v / 100) * 180;
  const polar = (deg: number, radius: number = r) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(a), y: cy - radius * Math.sin(a) };
  };
  const arcPath = (a: number, b: number) => {
    const p1 = polar(toAngle(a));
    const p2 = polar(toAngle(b));
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y}`;
  };
  const currentTier =
    QUALITY_TIERS.find((t) => t.grade === grade) ?? QUALITY_TIERS[0];
  // Segmented tier bands with tiny gaps for visible bifurcation.
  const segs = QUALITY_TIERS.map((t, i) => {
    const nextMin =
      i < QUALITY_TIERS.length - 1 ? QUALITY_TIERS[i + 1].min : 100;
    const gap = 0.1;
    const startAdj = i === 0 ? 0 : gap / 2;
    const endAdj = i === QUALITY_TIERS.length - 1 ? 0 : gap / 2;
    return { a: t.min + startAdj, b: nextMin - endAdj, c: t.color };
  });
  // Needle geometry: tapered triangle in tier color.
  const needleAngleDeg = toAngle(s);
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180;
  const tip = polar(needleAngleDeg, r - sw / 2 - 4);
  const baseHalf = 7;
  const baseLeft = {
    x: cx + baseHalf * Math.cos(needleAngleRad + Math.PI / 2),
    y: cy - baseHalf * Math.sin(needleAngleRad + Math.PI / 2),
  };
  const baseRight = {
    x: cx + baseHalf * Math.cos(needleAngleRad - Math.PI / 2),
    y: cy - baseHalf * Math.sin(needleAngleRad - Math.PI / 2),
  };
  const needlePath = `M ${baseLeft.x} ${baseLeft.y} L ${tip.x} ${tip.y} L ${baseRight.x} ${baseRight.y} Z`;
  return (
    <svg viewBox="-5 -8 310 178" className="score-gauge-svg">
      <defs>
        <filter id="needleShadow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" />
          <feOffset dx="0" dy="2.5" result="off" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.35" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Segmented tier bands */}
      {segs.map((g, i) => (
        <path
          key={i}
          d={arcPath(g.a, g.b)}
          stroke={g.c}
          strokeWidth={sw}
          fill="none"
          strokeLinecap={i === 0 || i === segs.length - 1 ? "round" : "butt"}
        />
      ))}
      {/* Needle */}
      <path
        d={needlePath}
        fill={currentTier.color}
        filter="url(#needleShadow)"
      />
      {/* Hub */}
      <circle
        cx={cx}
        cy={cy}
        r={11}
        fill={currentTier.color}
        filter="url(#needleShadow)"
      />
      <circle cx={cx} cy={cy} r={5} fill="#fff" />
    </svg>
  );
};

// Per-signal max points — mirrors backend blogScorer rubric (URL) and blogContentScorer (text).
// Used to compute bucket percentages when backend breakdown only ships earned values.
const SIGNAL_MAX_URL: Record<string, number> = {
  wordCount: 7,
  originalityDepth: 8,
  factualAccuracy: 4,
  metaTags: 5,
  headingStructure: 4,
  singleH1: 2,
  questionH2s: 2,
  primaryKeywordClarity: 1,
  faqContentSection: 4,
  faqPageSchema: 2,
  noGenericFaqs: 2,
  aiBotAccess: 6,
  llmsTxt: 3,
  entityDensity: 3,
  personSchema: 2,
  howToSchema: 1,
  itemListSchema: 1,
  speakableSchema: 1,
  directAnswerIntro: 3,
  tldrBlock: 2,
  tableOfContents: 2,
  comparisonTable: 2,
  anchorLinks: 1,
  internalLinks: 2,
  outboundAuthorityLinks: 2,
  pillarLinking: 1,
  authorBio: 2,
  reviewerLine: 1,
  authorArchivePageLink: 1,
  imageAltText: 2,
  uniqueVisuals: 2,
  downloadableAssets: 1,
  firstHandExperience: 3,
  originalExamples: 2,
  caseStudyCallouts: 1,
  noFictionalQuotes: 1,
  avoidWeakClaims: 1,
  naturalKeywords: 1,
  noEmDashes: 1,
  readingTime: 1,
  changelogBlock: 1,
};

const SIGNAL_MAX_TEXT: Record<string, number> = {
  originalityDepth: 10,
  firstHandExperience: 8,
  factualAccuracy: 6,
  originalExamples: 6,
  caseStudyCallouts: 5,
  headingStructure: 5,
  questionH2s: 4,
  directAnswerIntro: 5,
  tldrBlock: 4,
  faqContentSection: 4,
  noGenericFaqs: 3,
  writingClarity: 8,
  naturalKeywords: 3,
  avoidWeakClaims: 3,
  noEmDashes: 3,
  noFictionalQuotes: 3,
  hookStrength: 5,
  conclusionStrength: 3,
  comparisonStructure: 2,
  wordCount: 5,
  entityDensity: 3,
  statisticalClaims: 2,
};

// 5 user-friendly buckets. Collapses backend's 13 rubric categories (A-M).
// Each bucket lists ALL signal keys it may contain across URL + text modes.
// Bucket auto-hides if the current scan has no signals from its list.
type Bucket = {
  key: string;
  label: string;
  icon: string;
  color: string;
  signals: string[];
};

const BUCKETS: Bucket[] = [
  {
    key: "content",
    label: "Content Quality",
    icon: "📝",
    color: "#10b981",
    signals: [
      "wordCount",
      "originalityDepth",
      "firstHandExperience",
      "originalExamples",
      "caseStudyCallouts",
      "entityDensity",
    ],
  },
  {
    key: "structure",
    label: "Structure & Hooks",
    icon: "🏗️",
    color: "#ff7c02",
    signals: [
      "metaTags",
      "headingStructure",
      "singleH1",
      "questionH2s",
      "primaryKeywordClarity",
      "directAnswerIntro",
      "tldrBlock",
      "tableOfContents",
      "comparisonTable",
      "hookStrength",
      "conclusionStrength",
      "comparisonStructure",
    ],
  },
  {
    key: "schema",
    label: "Schema & AI Access",
    icon: "🔗",
    color: "#0ea5e9",
    signals: [
      "aiBotAccess",
      "llmsTxt",
      "personSchema",
      "howToSchema",
      "itemListSchema",
      "speakableSchema",
    ],
  },
  {
    key: "authority",
    label: "FAQ & Authority",
    icon: "🛡️",
    color: "#8b5cf6",
    signals: [
      "faqContentSection",
      "faqPageSchema",
      "noGenericFaqs",
      "factualAccuracy",
      "authorBio",
      "reviewerLine",
      "authorArchivePageLink",
      "statisticalClaims",
    ],
  },
  {
    key: "style",
    label: "Style & Freshness",
    icon: "✨",
    color: "#f59e0b",
    signals: [
      "anchorLinks",
      "internalLinks",
      "outboundAuthorityLinks",
      "pillarLinking",
      "imageAltText",
      "uniqueVisuals",
      "downloadableAssets",
      "noFictionalQuotes",
      "avoidWeakClaims",
      "naturalKeywords",
      "noEmDashes",
      "readingTime",
      "changelogBlock",
      "writingClarity",
    ],
  },
];

const computeBucketStats = (
  bucket: Bucket,
  breakdown: Record<string, number>,
  inputType: "url" | "text",
) => {
  const maxes = inputType === "text" ? SIGNAL_MAX_TEXT : SIGNAL_MAX_URL;
  let earned = 0;
  let max = 0;
  for (const key of bucket.signals) {
    if (key in breakdown) {
      const cap = maxes[key] ?? 0;
      // Clamp per-signal earned to its rubric cap — defends against backend rounding
      // errors that emit values slightly above the cap (e.g. 3.5 for a 3-pt signal).
      const raw = breakdown[key] ?? 0;
      earned += Math.min(raw, cap);
      max += cap;
    }
  }
  return { earned, max };
};

// ── Progressive Loading Card ──────────────────────────────────────────────────
// Backend stages are deterministic in timing (scrape ~5s → verifier+scorer
// ~25-45s → db <1s). We don't stream from the server — we just cycle through
// stage labels based on elapsed time so the user sees activity instead of a
// static spinner for 30-50 seconds. Elapsed counter proves the request hasn't
// stalled. ETA decreases to set expectation.

type LoadingStage = { label: string; icon: string; minSec: number };

const URL_STAGES: LoadingStage[] = [
  { label: "Fetching live page content...", icon: "📡", minSec: 0 },
  { label: "Parsing HTML, schemas & metadata...", icon: "🔍", minSec: 4 },
  { label: "Cross-checking signals...", icon: "🧠", minSec: 8 },
  { label: "AI scoring 42 signals...", icon: "🤖", minSec: 12 },
  { label: "Almost done — finalizing audit report...", icon: "✨", minSec: 40 },
];

const TEXT_STAGES: LoadingStage[] = [
  { label: "Normalizing pasted content...", icon: "📝", minSec: 0 },
  {
    label: "Extracting parser signals (entities, claims)...",
    icon: "🔍",
    minSec: 2,
  },
  {
    label: "AI scoring 22 content signals — substance, hooks...",
    icon: "🤖",
    minSec: 5,
  },
  { label: "Almost done — finalizing audit report...", icon: "✨", minSec: 25 },
];

const ProgressiveLoadingCard: React.FC<{ mode: "url" | "text" }> = ({
  mode,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const stages = mode === "url" ? URL_STAGES : TEXT_STAGES;
  const expectedTotal = mode === "url" ? 50 : 30;
  // Text-mode audits skip Schema & AI Access (no HTML/robots/schema signals apply).
  const visibleBuckets =
    mode === "url" ? BUCKETS : BUCKETS.filter((b) => b.key !== "schema");

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      500,
    );
    return () => clearInterval(tick);
  }, []);

  // Find latest stage we've passed.
  const currentStage =
    stages
      .slice()
      .reverse()
      .find((s) => elapsed >= s.minSec) || stages[0];
  const progressPct = Math.min(99, Math.round((elapsed / expectedTotal) * 100));
  const remaining = Math.max(0, expectedTotal - elapsed);

  // Cycle through category "focus" tags so the user sees which bucket is being
  // evaluated. Roughly matches backend order: parser signals first (structure /
  // schema / access), then AI scoring (content / authority / style).
  const activeBucketIdx = Math.min(
    visibleBuckets.length - 1,
    Math.floor(
      elapsed / Math.max(1, Math.round(expectedTotal / visibleBuckets.length)),
    ),
  );

  // Universal status colors — same across all rows for visual consistency.
  const DONE_COLOR = "#10b981"; // green — universal "success" for all completed rows
  const ACTIVE_COLOR = "#ff7c02"; // brand orange — active row

  return (
    <div className="geo-loading-card">
      <div style={{ width: "100%", maxWidth: 560, margin: "0 auto" }}>
        {/* Header — overall status + progress ring */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Progress
            type="circle"
            percent={progressPct}
            strokeColor={{ "0%": "#ffb03a", "100%": ACTIVE_COLOR }}
            trailColor="#f1f5f9"
            size={96}
            strokeWidth={8}
            format={(p) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.1,
                }}
              >
                <span
                  style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}
                >
                  {p}%
                </span>
                <span
                  style={{
                    fontSize: 9,
                    color: "#94a3b8",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Progress
                </span>
              </div>
            )}
          />
          <h3
            style={{
              margin: "18px 0 0 0",
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Auditing your blog
          </h3>
        </div>

        {/* Category-by-category status list */}
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            background: "#fff",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
          }}
        >
          {visibleBuckets.map((b, i) => {
            const isActive = i === activeBucketIdx;
            const isDone = i < activeBucketIdx;
            const isLast = i === visibleBuckets.length - 1;
            return (
              <div
                key={b.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 18px",
                  borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                  background: isActive ? `${ACTIVE_COLOR}0a` : "transparent",
                  transition: "background 0.25s ease",
                  position: "relative",
                }}
              >
                {/* Left: status indicator */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDone
                      ? DONE_COLOR
                      : isActive
                        ? `${ACTIVE_COLOR}22`
                        : "#f1f5f9",
                    color: isDone
                      ? "#fff"
                      : isActive
                        ? ACTIVE_COLOR
                        : "#94a3b8",
                    flexShrink: 0,
                    transition: "all 0.25s ease",
                  }}
                >
                  {isDone ? (
                    <CheckCircleOutlined style={{ fontSize: 16 }} />
                  ) : isActive ? (
                    <Spin size="small" />
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 700 }}>
                      {i + 1}
                    </span>
                  )}
                </div>

                {/* Middle: category label */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: isActive
                        ? "#0f172a"
                        : isDone
                          ? "#475569"
                          : "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{b.icon}</span>
                    <span>{b.label}</span>
                  </div>
                </div>

                {/* Right: status text */}
                <div style={{ flexShrink: 0 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: isDone
                        ? DONE_COLOR
                        : isActive
                          ? ACTIVE_COLOR
                          : "#cbd5e1",
                    }}
                  >
                    {isDone ? "Completed" : isActive ? "In progress" : "Queued"}
                  </span>
                </div>

                {/* Active row: bottom progress line */}
                {isActive && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 2,
                      background: "#f1f5f9",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: "40%",
                        background: ACTIVE_COLOR,
                        animation: "geoLoaderSlide 1.6s ease-in-out infinite",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Sliding-bar keyframes for the active row */}
      <style>{`
        @keyframes geoLoaderSlide {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(120%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const GeoScan: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"url" | "text">("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  // Distinct from `loading`: used only when hydrating a previously-run scan by id
  // (audit history click or direct URL). Avoids showing the fresh-scan "Auditing your blog"
  // progress UI when we're really just fetching stored details.
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── Runtime mode detection ────────────────────────────────────────────────
  // Anonymous → freemium (public endpoints + reCAPTCHA + $0.99 unlock).
  // Logged-in → paid (auth endpoints + credit deduction + saved history).
  const location = useLocation();
  const isPaid = useIsAuthenticated();
  const FREE_MODE = !isPaid;

  // ── Paid credit state (from PrivateLayout's /plan-details fetch) ──────────
  const { planDetails, setPlanDetails }: any = useContext(GlobalContext);
  const auditLimit = planDetails?.blogAuditLimit ?? 0;
  const auditUsed = planDetails?.blogAuditUsed ?? 0;
  const auditRemaining = Math.max(auditLimit - auditUsed, 0);
  const noCredits = isPaid && auditLimit > 0 && auditRemaining <= 0;

  const refreshPlanDetails = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("accessToken");
      if (!userId || !token) return;
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/plan-details?userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlanDetails({
        ...res.data,
        maxCount: res.data.name === "Free" ? 2 : res.data.name === "Basic" ? 4 : 10,
      });
    } catch (e) {
      // Non-fatal — badge stays stale until next nav.
    }
  };

  const [unlocked, setUnlocked] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [unlockLoading, setUnlockLoading] = useState(false);

  // Auto-redirect logged-in users off the public URL. Preserves ?prefillId
  // (WordPress bridge) but drops any free-scan :id since IDs live in a
  // different table (free_geo_scans vs geo_scans) and would 404.
  useEffect(() => {
    const onPublicRoute = location.pathname.startsWith("/blog-auditor-public");
    if (isPaid && onPublicRoute) {
      const search = location.search.includes("prefillId")
        ? location.search
        : "";
      navigate(`/blog-auditor${search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, location.pathname]);

  const fetchHistory = async () => {
    if (!isPaid) return; // guests have no persistent history
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/geo-scans?limit=20`, {
        headers: authHeader(),
      });
      setHistory(res.data?.scans || []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    if (!isPaid) preloadRecaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid]);

  const loadScanDetails = async (scanId: string) => {
    setDetailsLoading(true);
    // Snapshot mode at call time — avoids mid-request auth flip picking
    // the wrong response shape.
    const modeAtCall = isAuthenticated() ? "paid" : "free";
    try {
      const endpoint =
        modeAtCall === "free"
          ? `${API}/public/geo-scan/${scanId}`
          : `${API}/geo-scans/${scanId}`;
      const headers = modeAtCall === "free" ? publicHeader() : authHeader();
      const res = await axios.get(endpoint, { headers });
      // Public endpoint returns flat scan payload; paid returns { scan: {...} }
      const s = modeAtCall === "free" ? res.data : res.data.scan;
      if (modeAtCall === "free") {
        setResult({
          scanId: s.scanId,
          inputType: s.inputType,
          score: {
            blogScore: s.score?.blogScore ?? 0,
            grade: s.score?.grade ?? "C",
            summary: s.score?.summary ?? "",
            breakdown: s.score?.breakdown ?? {},
          },
          signals: s.signals || {},
          suggestions: Array.isArray(s.suggestions) ? s.suggestions : [],
          content: {
            title: s.content?.title ?? "",
            wordCount: s.content?.wordCount ?? 0,
            siteName: s.content?.siteName ?? null,
            byline: s.content?.byline ?? null,
          },
        });
        setUnlocked(!s.locked);
      } else {
        setResult({
          scanId: s.id,
          inputType: s.inputType,
          score: {
            blogScore: s.blogScore ?? s.geoScore ?? 0,
            grade: s.grade ?? "C",
            summary: s.summary ?? "",
            breakdown: s.scoreBreakdown ?? {},
          },
          signals: {},
          suggestions: s.suggestions ?? [],
          content: {
            title: s.contentTitle ?? "",
            wordCount: s.wordCount ?? 0,
            siteName: null,
            byline: null,
          },
        });
      }
    } catch {
      message.error("Failed to load scan");
      navigate(isPaid ? "/blog-auditor" : "/blog-auditor-public");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (id && result?.scanId !== id) {
      loadScanDetails(id);
      setSelectedBucket(null);
    } else if (!id) {
      setResult(null);
      setSelectedBucket(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── WordPress prefill bridge ─────────────────────────────────────────────
  // When user lands with ?prefillId=<uuid> (redirected from WP form action),
  // fetch the stored {type, content} once, populate the correct tab/input,
  // strip the query param, then trigger the scan automatically.
  const [autoScanCounter, setAutoScanCounter] = useState(0);
  const [prefillHandled, setPrefillHandled] = useState(false);

  useEffect(() => {
    if (id) return; // result-view URL — prefill does not apply
    if (prefillHandled) return;
    // If paid user is on the public route, the auto-redirect effect is about
    // to move them to /blog-auditor. Skip the prefill fetch here — it would
    // consume the token, then the remount on the paid route would re-fetch
    // and see 410 "already consumed". Let the paid-route mount handle it.
    if (isPaid && location.pathname.startsWith("/blog-auditor-public")) return;
    const params = new URLSearchParams(location.search);
    const prefillId = params.get("prefillId");
    if (!prefillId) return;
    setPrefillHandled(true);

    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/wp-prefill/${prefillId}`
        );
        if (data.type === "url") {
          setActiveTab("url");
          setUrl(data.content);
        } else {
          setActiveTab("text");
          setText(data.content);
        }
        const basePath = isPaid ? "/blog-auditor" : "/blog-auditor-public";
        navigate(basePath, { replace: true });
        setAutoScanCounter((c) => c + 1);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 410) {
          message.error("This prefill link has expired or was already used");
        } else if (status === 404) {
          message.error("Prefill link not found");
        } else {
          message.error("Failed to load prefilled content");
        }
        const basePath = isPaid ? "/blog-auditor" : "/blog-auditor-public";
        navigate(basePath, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, isPaid, id]);

  useEffect(() => {
    if (autoScanCounter === 0) return;
    handleScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScanCounter]);

  const handleScan = async () => {
    const payload: { url?: string; text?: string; recaptchaToken?: string } = {};
    if (activeTab === "url") {
      const trimmed = url.trim();
      if (!trimmed) return message.warning("Enter a URL");
      payload.url = trimmed;
    } else {
      const trimmed = text.trim();
      if (!trimmed) return message.warning("Paste some content");
      if (trimmed.length < 100)
        return message.warning("Content too short — need 100+ characters");
      payload.text = trimmed;
    }

    setLoading(true);
    setUnlocked(false);
    // Fresh auth read — supports mid-session login (anonymous starts scan,
    // logs in on another tab, next scan hits paid endpoint automatically).
    const paidAtCall = isAuthenticated();
    try {
      if (!paidAtCall) {
        try {
          payload.recaptchaToken = await executeRecaptcha("blog_auditor_scan");
        } catch {
          setLoading(false);
          return message.error("Security check failed. Refresh and try again.");
        }
      }
      const endpoint = paidAtCall ? `${API}/geo-scan` : `${API}/public/geo-scan`;
      const headers = paidAtCall ? authHeader() : publicHeader();
      const res = await axios.post(endpoint, payload, { headers });
      const d = res.data;
      // Free mode returns suggestions=null when locked. Coerce to [] so
      // filter/map calls don't crash. The lock overlay hides content anyway.
      if (!paidAtCall && d.locked && !Array.isArray(d.suggestions)) {
        d.suggestions = [];
      }
      message.success("Scan complete!");
      // Batch all state updates in one sync segment BEFORE navigate — prevents
      // paint gap where (loading=false, result=null) would flash the InputPanel.
      setResult(d);
      setSelectedBucket(null);
      setLoading(false);
      fetchHistory();
      if (paidAtCall) refreshPlanDetails();
      const basePath = paidAtCall ? "/blog-auditor" : "/blog-auditor-public";
      navigate(`${basePath}/${d.scanId}`, { replace: true });
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === "NO_AUDIT_CREDITS") {
        message.warning({
          content: "No Blog Auditor credits remaining. Purchase an add-on to continue.",
          key: "no-audit-credits",
        });
        refreshPlanDetails();
      } else {
        message.error(
          err?.response?.data?.message || err?.message || "Scan failed",
        );
      }
      setLoading(false);
    }
  };

  const openHistoryItem = (scanId: string) => {
    setHistoryModalOpen(false);
    navigate(`/blog-auditor/${scanId}`);
  };

  const slugify = (input: string) => {
    const base = (input || "audit")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
    return base || "audit";
  };

  const handleDownloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      // Lazy-load @react-pdf/renderer + report component to keep initial bundle small (~400KB deferred).
      const [{ pdf }, { default: GeoScanPdfReport }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./GeoScanPdfReport"),
      ]);
      const bucketStats = BUCKETS.map((b) => {
        const s = computeBucketStats(
          b,
          result.score.breakdown || {},
          result.inputType,
        );
        if (s.max === 0) return null;
        return {
          label: b.label,
          earned: s.earned,
          max: s.max,
          pct: Math.round((s.earned / s.max) * 100),
        };
      }).filter(
        (x): x is { label: string; earned: number; max: number; pct: number } =>
          x !== null,
      );
      const qualityLabel = getQualityTier(result.score.grade).label;
      const generatedAt = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      const blob = await pdf(
        <GeoScanPdfReport
          result={result}
          qualityLabel={qualityLabel}
          buckets={bucketStats}
          signalLabels={SIGNAL_LABELS}
          generatedAt={generatedAt}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `blog-audit-${slugify(result.content.title)}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── FREEMIUM handlers (no-op when isPaid=true) ─────────────────────────────
  const handleSubmitUnlock = async () => {
    if (!result) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return message.warning("Enter a valid email");
    }
    setUnlockLoading(true);
    try {
      const res = await axios.post(
        `${API}/public/geo-unlock`,
        { scanId: result.scanId, email: trimmed },
        { headers: publicHeader() },
      );
      const data = res.data || {};

      // Already-paid path: server hands back HMAC token → unlock in-app.
      if (data.alreadyPurchased && data.unlockToken) {
        setShowEmailModal(false);
        setUnlockLoading(false);
        // Rewrite URL with paid=1&token=&email= so useEffect handles the rest
        // and a shared/reloaded link keeps working.
        const params = new URLSearchParams({
          paid: "1",
          token: data.unlockToken,
          email: data.email || trimmed.toLowerCase(),
        });
        navigate(`/blog-auditor-public/${result.scanId}?${params.toString()}`, { replace: true });
        message.info("You've already unlocked this report. Check your email for the PDF.", 5);
        return;
      }

      const { checkoutUrl } = data;
      if (!checkoutUrl) throw new Error("No checkoutUrl returned");
      window.location.href = checkoutUrl;
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || "Failed to start checkout",
      );
      setUnlockLoading(false);
    }
  };

  // On mount / URL change, if landing on success_url with token+email, fetch
  // full unlocked report and inject suggestions into current result.
  useEffect(() => {
    if (isPaid) return; // paid users never see unlock flow
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const emailParam = params.get("email");
    const paid = params.get("paid");
    const scanIdFromRoute = id;
    if (!paid || !token || !emailParam || !scanIdFromRoute) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API}/public/geo-report/${scanIdFromRoute}`,
          {
            params: { token, email: emailParam },
            headers: publicHeader(),
          },
        );
        const d = res.data;
        setResult((prev) =>
          prev
            ? { ...prev, suggestions: d.suggestions || [], ...d }
            : { ...d, suggestions: d.suggestions || [] },
        );
        setUnlocked(true);
        message.success("Report unlocked! PDF also sent to your email.", 5);
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || err?.message || "Failed to unlock report",
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, id]);

  const displayScore = result?.score.blogScore ?? 0;
  const displayGrade = result?.score.grade ?? "F";
  const isLocked = !isPaid && !unlocked;

  return (
    <div className="geo-scan-page">
      {/* ── Page Header ── */}
      <div className="geo-scan-header-section">
        <div className="header-left">
          {result && (
            <div
              className="back-to-auditor-link"
              onClick={() => {
                navigate(isPaid ? "/blog-auditor" : "/blog-auditor-public");
                setUrl("");
                setText("");
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13.5px",
                fontWeight: 600,
                color: "#ff7c02",
                cursor: "pointer",
                marginBottom: "10px",
                transition: "all 0.2s ease",
              }}
            >
              <ArrowLeftOutlined /> Back to Auditor
            </div>
          )}
          <h1
            className={
              result ? "header-title header-title--article" : "header-title"
            }
          >
            {!result && <AuditorBrandIcon />}
            <span className="header-title-text">
              {result
                ? result.content.title || "Untitled Article"
                : "Blog Auditor"}
            </span>
          </h1>
          <p className="header-subtitle">
            {result ? (
              <>
                {result.inputType === "url"
                  ? "Live URL audit"
                  : "Pasted-content audit"}
                {result.content.siteName ? (
                  <>
                    {" "}
                    · <strong>{result.content.siteName}</strong>
                  </>
                ) : null}
                {result.content.byline ? (
                  <> · by {result.content.byline}</>
                ) : null}
                {typeof result.content.wordCount === "number" ? (
                  <> · {result.content.wordCount.toLocaleString()} words</>
                ) : null}
              </>
            ) : (
              "Analyze your content instantly against search quality standards & Google E-E-A-T guidelines."
            )}
          </p>
        </div>
        <div
          className="header-actions"
          style={{ display: "flex", gap: "12px", alignItems: "center" }}
        >
          {/* Audit History — hidden for guests (no persistent history) */}
          {isPaid && !result && !id && !loading && (
            <Button
              type="default"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryModalOpen(true)}
              className="history-trigger-btn"
            >
              Audit History ({history.length})
            </Button>
          )}
          {/* Download PDF — only when viewing a specific scan */}
          {result && !isLocked && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPdf}
              loading={pdfLoading}
              className="pdf-download-btn"
            >
              Download PDF
            </Button>
          )}
          {result && isLocked && (
            <Button
              type="primary"
              icon={<UnlockOutlined />}
              onClick={() => setShowEmailModal(true)}
              className="pdf-download-btn"
              style={{ background: "#ff7c02", borderColor: "#ff7c02" }}
            >
              Unlock Report
            </Button>
          )}
        </div>
      </div>

      {/* ── Input panel ── */}
      {!result && !loading && !id && (
        <div className="geo-input-container">
          <Tabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as "url" | "text")}
            className="geo-tabs"
            items={[
              {
                key: "url",
                label: (
                  <span className="tab-label">
                    <LinkOutlined /> Analyze Live URL
                  </span>
                ),
                children: (
                  <div className="tab-content-block">
                    <Input
                      size="large"
                      placeholder="https://yourwebsite.com/blog/article-slug"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onPressEnter={handleScan}
                      disabled={loading}
                      className="custom-url-input"
                    />
                    <span className="input-hint-text">
                      We'll fetch, parse, and analyze the full live HTML of this
                      URL.
                    </span>
                  </div>
                ),
              },
              {
                key: "text",
                label: (
                  <span className="tab-label">
                    <FileTextOutlined /> Paste Content
                  </span>
                ),
                children: (
                  <div className="tab-content-block">
                    <Input.TextArea
                      rows={6}
                      placeholder="Paste your raw article draft text or full HTML content here..."
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      disabled={loading}
                      showCount
                      maxLength={200000}
                      className="custom-text-area"
                    />
                    <span className="input-hint-text">
                      Paste raw HTML for perfect structural parsing, or plain
                      text for quality scoring.
                    </span>
                  </div>
                ),
              },
            ]}
          />
          <div className="geo-action-bar">
            {noCredits ? (
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => navigate("/account")}
                className="run-audit-btn"
              >
                Buy Audit Credits
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                loading={loading}
                onClick={handleScan}
                className="run-audit-btn"
              >
                {loading ? "Analyzing Content..." : "Start Audit"}
              </Button>
            )}
          </div>
          {!isPaid && (
            <p className="recaptcha-notice">
              This site is protected by reCAPTCHA and the Google{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{" "}
              apply.
            </p>
          )}
        </div>
      )}

      {/* ── Fresh scan: progressive "Auditing your blog" UI ── */}
      {loading && !detailsLoading && !(id && !result) && (
        <ProgressiveLoadingCard mode={activeTab} />
      )}

      {/* ── Hydrating a previously-run scan by id: lightweight skeleton, no fake progress ── */}
      {(detailsLoading || (id && !result && !loading)) && (
        <div className="geo-details-skeleton">
          <Skeleton active paragraph={{ rows: 4 }} title />
          <div style={{ height: 12 }} />
          <Skeleton.Button
            active
            block
            style={{ height: 120, borderRadius: 12 }}
          />
          <div style={{ height: 12 }} />
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </div>
      )}

      {/* ── Two-Column Results Dashboard ── */}
      {result && (
        <div className="geo-dashboard-layout">
          {/* Left Column: Metric Overview */}
          <div className="geo-dash-sidebar">
            {/* Score Ring Card */}
            <div className="geo-metric-card score-hero">
              <h3 className="score-heading">Blog Rating</h3>

              {/* Speedometer gauge with tier chips on top of arc */}
              <div className="gauge-wrap">
                <ScoreGauge score={displayScore} grade={displayGrade} />
              </div>

              {/* Big hero score */}
              <div className="hero-score-display">
                <span
                  className="hero-score-num"
                  style={{ color: getGradeColor(displayGrade) }}
                >
                  {displayScore}
                </span>
                <span className="hero-score-suffix">/100</span>
              </div>

              <div
                className="hero-score-grade"
                style={{ background: getGradeColor(displayGrade) }}
              >
                <span>{getQualityTier(displayGrade).label}</span>
                <Popover
                  placement="right"
                  overlayClassName="quality-tier-popover"
                  title={
                    <span style={{ fontWeight: 600 }}>Score Quality Tiers</span>
                  }
                  content={
                    <div style={{ maxWidth: 280 }}>
                      {[...QUALITY_TIERS].reverse().map((t) => {
                        const isCurrent = t.grade === displayGrade;
                        return (
                          <div
                            key={t.grade}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "6px 8px",
                              borderRadius: 6,
                              background: isCurrent
                                ? `${t.color}20`
                                : "transparent",
                              border: isCurrent
                                ? `1px solid ${t.color}`
                                : "1px solid transparent",
                              marginBottom: 4,
                            }}
                          >
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: t.color,
                                flexShrink: 0,
                              }}
                            />
                            <div style={{ flex: 1, fontSize: 13 }}>
                              <div
                                style={{ fontWeight: isCurrent ? 700 : 500 }}
                              >
                                {t.label}{" "}
                                <span
                                  style={{ color: "#64748b", fontWeight: 400 }}
                                >
                                  ({t.range})
                                </span>
                                {isCurrent && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      color: t.color,
                                      fontSize: 11,
                                    }}
                                  >
                                    ← you are here
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {(() => {
                        const nextTier = getNextTierGap(displayScore);
                        const currentTip = getQualityTier(displayGrade).tip;
                        return (
                          <div
                            style={{
                              marginTop: 8,
                              paddingTop: 8,
                              borderTop: "1px solid #e2e8f0",
                              fontSize: 12,
                              color: "#475569",
                            }}
                          >
                            <div style={{ marginBottom: 4 }}>
                              <strong>How to improve:</strong> {currentTip}
                            </div>
                            {nextTier && (
                              <div style={{ color: "#0ea5e9" }}>
                                <strong>{nextTier.gap} more points</strong> to
                                reach <strong>{nextTier.label}</strong>.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  }
                >
                  <InfoCircleOutlined
                    className="grade-info-icon"
                    style={{ marginLeft: 6, cursor: "pointer", opacity: 0.85 }}
                  />
                </Popover>
              </div>
            </div>

            {/* Document Info Card - Clean Vertical Rows to Prevent Layout Wrapping */}
            <div className="geo-metric-card document-info">
              <h3>Article Metadata</h3>

              {result.content.title && (
                <div className="info-item">
                  <span className="info-lbl">Title</span>
                  <p className="info-val" title={result.content.title}>
                    {result.content.title}
                  </p>
                </div>
              )}

              <div className="info-item">
                <span className="info-lbl">Word Count</span>
                <p className="info-val">
                  <FileWordOutlined /> {result.content.wordCount} words
                </p>
              </div>

              {result.content.siteName && (
                <div className="info-item">
                  <span className="info-lbl">Publisher</span>
                  <p className="info-val">
                    <GlobalOutlined /> {result.content.siteName}
                  </p>
                </div>
              )}

              <div className="audit-action-row">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    navigate(isPaid ? "/blog-auditor" : "/blog-auditor-public");
                    setUrl("");
                    setText("");
                  }}
                  className="audit-reset-btn"
                  block
                >
                  Audit Another
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: AI Analysis & Action Plan */}
          <div className="geo-dash-main">
            {/* Summary Box */}
            <div className="geo-feedback-card">
              <div className="feedback-header">
                <BulbOutlined className="feedback-icon" />
                <h3>AI Editorial Audit Summary</h3>
              </div>
              <p className="feedback-body">{result.score.summary}</p>
            </div>

            {/* Score by Category — 5 buckets collapsing 13 rubric categories */}
            <div
              className="geo-category-cards-wrapper"
              style={{ marginBottom: 20 }}
            >
              <div className="category-header" style={{ marginBottom: 14 }}>
                <h3
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  Score by Category
                </h3>
                <p style={{ margin: 0, fontSize: 12.5, color: "#64748b" }}>
                  {isLocked
                    ? "Unlock the full report to see prioritized fixes per category."
                    : "Click a category to filter the suggested improvements below."}
                </p>
              </div>
              {(() => {
                const visibleBuckets = BUCKETS.map((b) => ({
                  bucket: b,
                  stats: computeBucketStats(
                    b,
                    result.score.breakdown || {},
                    result.inputType,
                  ),
                })).filter((x) => x.stats.max > 0);
                return (
                  <div
                    className="category-cards-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
                      gap: 12,
                    }}
                  >
                    {visibleBuckets.map(({ bucket, stats }) => {
                      if (stats.max === 0) return null;
                      const pct = Math.round((stats.earned / stats.max) * 100);
                      const perfColor =
                        pct >= 80
                          ? "#10b981"
                          : pct >= 50
                            ? "#f59e0b"
                            : "#dc2626";
                      const isSelected = selectedBucket === bucket.key;
                      // Mini ring geometry
                      const ringR = 18;
                      const ringC = 2 * Math.PI * ringR;
                      const ringDash = (pct / 100) * ringC;
                      return (
                        <div
                          key={bucket.key}
                          className={`cat-card${isSelected ? " is-selected" : ""}${isLocked ? " is-locked" : ""}`}
                          onClick={() => {
                            if (isLocked) return;
                            setSelectedBucket(isSelected ? null : bucket.key);
                          }}
                          style={{
                            ["--bucket-color" as any]: bucket.color,
                            ["--perf-color" as any]: perfColor,
                            cursor: isLocked ? "default" : "pointer",
                          }}
                        >
                          <div className="cat-card-strip" />
                          <div className="cat-card-head">
                            <div className="cat-icon-tile">{bucket.icon}</div>
                            <div className="cat-title">{bucket.label}</div>
                          </div>
                          <div className="cat-card-body">
                            <div className="cat-score-block">
                              <span className="cat-score-num">
                                {stats.earned.toFixed(1)}
                              </span>
                              <span className="cat-score-max">
                                / {stats.max}
                              </span>
                            </div>
                            <div className="cat-ring-wrap">
                              <svg viewBox="0 0 44 44" className="cat-ring">
                                <circle
                                  cx="22"
                                  cy="22"
                                  r={ringR}
                                  stroke="#eef2f7"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <circle
                                  cx="22"
                                  cy="22"
                                  r={ringR}
                                  stroke={perfColor}
                                  strokeWidth="4"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeDasharray={`${ringDash} ${ringC}`}
                                  transform="rotate(-90 22 22)"
                                  style={{
                                    transition: "stroke-dasharray 0.6s ease",
                                  }}
                                />
                              </svg>
                              <span className="cat-ring-pct">{pct}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Improvements Grid */}
            {(() => {
              const activeBucket = selectedBucket
                ? BUCKETS.find((b) => b.key === selectedBucket)
                : null;
              const filteredSuggestions = activeBucket
                ? result.suggestions.filter((s) =>
                    activeBucket.signals.includes(s.signal),
                  )
                : result.suggestions;
              return (
                <div className="geo-improvements-card-wrapper">
                  <div className="improvements-header">
                    <h3>
                      Suggested Improvements ({isLocked ? ((result as any).suggestionCount ?? 5) : filteredSuggestions.length}{" "}
                      {(isLocked ? ((result as any).suggestionCount ?? 5) : filteredSuggestions.length) === 1 ? "fix" : "fixes"})
                      {activeBucket && (
                        <Tag
                          closable
                          onClose={(e) => {
                            e.preventDefault();
                            setSelectedBucket(null);
                          }}
                          color={activeBucket.color}
                          style={{ marginLeft: 8, verticalAlign: "middle" }}
                        >
                          {activeBucket.icon} {activeBucket.label}
                        </Tag>
                      )}
                    </h3>
                    <p>
                      {activeBucket
                        ? `Showing fixes for ${activeBucket.label}. Clear the filter to see all.`
                        : "Prioritized checklist to optimize formatting, trust signals, and SEO structure."}
                    </p>
                  </div>

                  {isLocked ? (
                    <div
                      style={{
                        position: "relative",
                        padding: "40px 24px",
                        background: "#f8fafc",
                        border: "2px dashed #cbd5e1",
                        borderRadius: 12,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ filter: "blur(6px)", opacity: 0.55, pointerEvents: "none", marginBottom: 24 }}>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 12, textAlign: "left" }}>
                          <strong>1. Add TL;DR block</strong>
                          <p>A short summary at the top of the article helps LLMs and readers grasp the main takeaway...</p>
                        </div>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 12, textAlign: "left" }}>
                          <strong>2. Include Author Bio</strong>
                          <p>Trust signals boost credibility. Add a byline block with expertise and links...</p>
                        </div>
                        <div style={{ background: "#fff", padding: 16, borderRadius: 8, textAlign: "left" }}>
                          <strong>3. Add FAQ section</strong>
                          <p>Structured Q&A helps answer engines quote directly...</p>
                        </div>
                      </div>
                      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, transform: "translateY(-50%)" }}>
                        <div style={{ fontSize: 44, marginBottom: 12 }}>🔒</div>
                        <h3 style={{ marginBottom: 8 }}>Full suggestions locked</h3>
                        <p style={{ color: "#64748b", maxWidth: 380, margin: "0 auto 16px" }}>
                          Unlock all prioritized fixes + downloadable PDF report emailed to you.
                        </p>
                        <Button
                          type="primary"
                          size="large"
                          icon={<UnlockOutlined />}
                          style={{ background: "#ff7c02", borderColor: "#ff7c02" }}
                          onClick={() => setShowEmailModal(true)}
                        >
                          Unlock for $0.99
                        </Button>
                      </div>
                    </div>
                  ) : filteredSuggestions.length === 0 ? (
                    <Empty
                      description={
                        activeBucket
                          ? `No open recommendations in ${activeBucket.label}. Great work here!`
                          : "Fantastic! Your article hits all key optimization parameters."
                      }
                    />
                  ) : (
                    <div className="improvements-list">
                      {filteredSuggestions.slice(0, 5).map((imp, i) => {
                        const badge = getPriorityBadge(imp.priority);
                        return (
                          <div
                            key={i}
                            className={`improvement-row-card ${imp.priority.toLowerCase()}`}
                          >
                            <div className="imp-card-left">
                              <span className="imp-number">{i + 1}</span>
                            </div>
                            <div className="imp-card-content">
                              <div className="imp-meta-row">
                                <span className="imp-signal-lbl">
                                  {SIGNAL_LABELS[imp.signal] || imp.signal}
                                </span>
                                <Tag
                                  color={badge.color}
                                  icon={badge.icon}
                                  className="imp-priority-tag"
                                >
                                  {imp.priority}
                                </Tag>
                              </div>
                              <p className="imp-fix-text">{imp.fix}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── FREEMIUM: Email capture modal → Stripe redirect ── */}
      <Modal
        title="Unlock Full Report"
        open={showEmailModal}
        onCancel={() => setShowEmailModal(false)}
        onOk={handleSubmitUnlock}
        okText={unlockLoading ? "Redirecting..." : "Continue"}
        okButtonProps={{ loading: unlockLoading, disabled: unlockLoading, style: { background: "#ff7c02", borderColor: "#ff7c02" } }}
        cancelButtonProps={{ disabled: unlockLoading }}
        centered
        destroyOnClose
      >
        <p style={{ color: "#475569", marginBottom: 16 }}>
          We'll email the full audit report (PDF) to this address after payment. No account needed.
        </p>
        <Input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="large"
          autoFocus
        />
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
          You'll be redirected to Stripe Checkout for a one-time $0.99 payment. Your email is used only to deliver the report.
        </p>
      </Modal>

      {/* ── History Modal — centered full-view, cleaner close X ── */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              paddingRight: 24,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Audit History
            </span>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 400 }}>
              {history.length} recent scan{history.length === 1 ? "" : "s"} ·
              Click a row to open the full report
            </span>
          </div>
        }
        open={historyModalOpen}
        onCancel={() => setHistoryModalOpen(false)}
        footer={null}
        width="min(1000px, 92vw)"
        centered
        className="history-modal"
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            padding: "16px 4px 4px",
          },
          header: {
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 12,
          },
        }}
        closeIcon={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "#f1f5f9",
              color: "#475569",
              fontSize: 14,
              transition: "all 0.15s ease",
            }}
          >
            ✕
          </span>
        }
      >
        {historyLoading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 40 }}
          >
            <Spin />
          </div>
        ) : history.length === 0 ? (
          <Empty description="No audits saved yet." style={{ marginTop: 60 }} />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
              gap: 10,
              padding: "0 12px",
            }}
          >
            {history.map((h) => {
              const score = h.blogScore ?? h.geoScore ?? 0;
              const scoreColor = getGradeColor(
                score >= 85
                  ? "A"
                  : score >= 70
                    ? "B"
                    : score >= 55
                      ? "C"
                      : score >= 40
                        ? "D"
                        : "F",
              );
              const qualityLabel =
                score >= 85
                  ? "Excellent"
                  : score >= 70
                    ? "Good"
                    : score >= 55
                      ? "Average"
                      : score >= 40
                        ? "Needs Work"
                        : "Poor";
              return (
                <div
                  key={h.id}
                  onClick={() => openHistoryItem(h.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    cursor: "pointer",
                    background: "#fff",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = scoreColor;
                    e.currentTarget.style.boxShadow = `0 2px 10px ${scoreColor}22`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* Score pill */}
                  <div
                    style={{
                      width: 52,
                      minWidth: 52,
                      height: 52,
                      borderRadius: 10,
                      background: `${scoreColor}18`,
                      border: `1.5px solid ${scoreColor}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: scoreColor,
                      }}
                    >
                      {score}
                    </span>
                    <span
                      style={{
                        fontSize: 8,
                        color: scoreColor,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        marginTop: 2,
                        textTransform: "uppercase",
                      }}
                    >
                      /100
                    </span>
                  </div>

                  {/* Meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginBottom: 4,
                      }}
                      title={h.contentTitle || h.inputUrl || "(Pasted Draft)"}
                    >
                      {h.contentTitle || h.inputUrl || "(Pasted Draft)"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 11,
                        color: "#64748b",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: scoreColor,
                          background: `${scoreColor}14`,
                          padding: "2px 6px",
                          borderRadius: 4,
                          letterSpacing: 0.3,
                        }}
                      >
                        {qualityLabel}
                      </span>
                      <span>·</span>
                      <span>{h.inputType.toUpperCase()}</span>
                      <span>·</span>
                      <span>{h.wordCount} w</span>
                      <span>·</span>
                      <span>{new Date(h.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <ArrowRightOutlined
                    style={{ color: "#94a3b8", fontSize: 14 }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GeoScan;
